import React, { useEffect, useState, useMemo } from 'react';
import api from '../../../../api';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const ClientEfficiency = ({ filters }) => {
  const { accessToken } = useAuth();
  const [clientData, setClientData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [focusMode, setFocusMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const rowsPerPage = 5;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
        const response = await api.get(`${baseUrl}/superAdminDashboard/getClientWiseEfficiency`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });
        setClientData(response.data?.data?.clientEfficiency || []);
      } catch (err) {
        console.error('Client Efficiency fetch error:', err);
        setClientData([]);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchData();
  }, [accessToken, filters]);
  const filteredData = clientData.filter((d) => d.clientName?.toLowerCase().includes(searchTerm.toLowerCase()));
  // const filteredData = useMemo(
  //   () => clientData.filter((d) => d.clientName?.toLowerCase().includes(searchTerm.toLowerCase())),
  //   [clientData, searchTerm]
  // );

  const sortedData = useMemo(() => {
    const sorted = [...filteredData];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        return typeof aVal === 'string'
          ? sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal)
          : sortConfig.direction === 'asc'
          ? aVal - bVal
          : bVal - aVal;
      });
    }
    return sorted;
  }, [filteredData, sortConfig]);

  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = sortedData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    else if (sortConfig.key === key && sortConfig.direction === 'desc') direction = '';
    setSortConfig({ key: direction ? key : '', direction });
  };

  const getSortIcon = (key) => {
    const base = 'w-4 h-4';
    if (sortConfig.key !== key) return <ChevronUpDownIcon className={`${base} text-gray-300`} />;
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className={`${base} text-[#edeeef]`} />
    ) : (
      <ChevronDownIcon className={`${base} text-[#edeeef]`} />
    );
  };

  const handleExportExcel = () => {
    const rows = clientData.map((d) => ({
      'Client Name': d.clientName,
      'Efficiency (%)': d.efficiencyPercentage,
      'Biometric Hours': d.biometricHours,
      'EPR Hours': d.eprHours,
      'Revenue Target': d.totalRevenueTarget,
      Achieved: d.totalAchieved,
      'Achieved (%)': d.achievedPercentage,
      'Quality (%)': d.qualityPercentage,
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Client Efficiency');
    XLSX.writeFile(wb, 'ClientEfficiency.xlsx');
  };

  const topThree = [...clientData]
    .sort((a, b) => parseFloat(b.efficiencyPercentage) - parseFloat(a.efficiencyPercentage))
    .slice(0, 3);

  const Pagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 text-sm rounded-full border font-semibold transition ${
            currentPage === i ? 'bg-[#1A2B5C] text-white border-[#1A2B5C]' : 'text-gray-600 border-gray-300 hover:bg-gray-100'
          }`}
        >
          {i}
        </button>
      );
    }
    return (
      <>
        {/* Showing message aligned to left */}
        {sortedData.length > 0 && (
          <div className="mt-4 text-sm text-gray-600 text-left">
            Showing {indexOfFirst + 1} to {Math.min(indexOfLast, sortedData.length)} of {sortedData.length} entries
          </div>
        )}

        {/* Pagination controls centered below */}
        <div className="flex flex-wrap justify-center items-center gap-2 mt-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {'<'}
          </button>
          {pages}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {'>'}
          </button>
        </div>
      </>
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <motion.div className="bg-white/60 backdrop-blur-xl border rounded-2xl shadow-xl p-4 sm:p-6 relative w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <h3 className="text-xl sm:text-2xl font-bold text-[#1A2B5C] text-center sm:text-left">Revenue Target To Volume Target</h3>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by client name..."
              className="border border-gray-300 rounded-full pl-4 pr-10 py-1.5 text-sm w-full focus:ring-2 focus:ring-[#1A2B5C] outline-none shadow"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 text-lg font-bold"
              >
                ×
              </button>
            )}
          </div>
          <motion.button
            whileHover={{ scale: clientData.length ? 1.05 : 1 }}
            whileTap={{ scale: clientData.length ? 0.95 : 1 }}
            onClick={() => clientData.length > 0 && setFocusMode(true)}
            disabled={clientData.length === 0}
            className={`px-4 py-2 text-sm rounded-full font-semibold shadow w-full sm:w-auto
              ${
                clientData.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#1A2B5C] text-white hover:opacity-90'
              }
            `}
          >
            Top 3
          </motion.button>
          {/* Export Button */}
          <motion.button
            whileHover={{ scale: clientData.length ? 1.05 : 1 }}
            whileTap={{ scale: clientData.length ? 0.95 : 1 }}
            onClick={handleExportExcel}
            disabled={clientData.length === 0}
            className={`px-4 py-2 text-sm rounded-full font-semibold shadow w-full sm:w-auto
    ${
      clientData.length === 0
        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
        : 'bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] text-white hover:opacity-90'
    }
  `}
          >
            Export to Excel
          </motion.button>
        </div>
      </div>

      {loading ? (
        <div className="min-h-[300px] flex justify-center items-center">
          <div className="animate-spin w-10 h-10 border-4 border-[#1A2B5C] border-t-[#EE1D23] rounded-full" />
        </div>
      ) : (
        <>
          {/* <div className="overflow-x-auto overflow-y-auto w-full rounded-xl bg-white shadow min-h-[400px]">
            <table className="min-w-max w-full text-sm text-gray-800 whitespace-nowrap  min-h-[400px]"> */}
          <div className="w-full overflow-x-auto rounded-xl bg-white shadow max-h-[600px] overflow-y-auto">
            <table className="min-w-[900px] w-full text-sm text-gray-800 whitespace-nowrap">
              <thead className="bg-[#1A2B5C] text-white text-md md:text-md sticky top-0">
                <tr>
                  {[
                    { key: 'clientName', label: 'Client Name' },
                    { key: 'efficiencyPercentage', label: 'Efficiency %' },
                    { key: 'biometricHours', label: 'Bio Hrs' },
                    { key: 'eprHours', label: 'EPR Hrs' },
                    { key: 'totalRevenueTarget', label: 'Revenue Target' },
                    { key: 'totalAchieved', label: 'Achieved' },
                    { key: 'achievedPercentage', label: 'Achieved %' },
                    { key: 'qualityPercentage', label: 'Quality %' },
                  ].map(({ key, label }) => (
                    <th key={key} onClick={() => handleSort(key)} className="px-4 py-2 font-semibold cursor-pointer">
                      <div className="flex items-center gap-2">
                        {label}
                        {getSortIcon(key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-base md:text-lg">
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="flex items-center justify-center h-[300px]">
                        <div className="flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-white via-[#f3f6fa] to-white/60 rounded-2xl px-6 py-10 shadow-inner border border-[#1A2B5C]/10 max-w-md mx-auto animate-glowIn">
                          {/* Unique Icon */}
                          <div className="relative">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-[#1A2B5C] to-[#EE1D23] flex items-center justify-center shadow-md">
                              <svg
                                className="w-8 h-8 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={1.8}
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M12 8v4l3 3m5-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <div className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full px-2 py-0.5 text-xs font-bold text-[#EE1D23] shadow">
                              !
                            </div>
                          </div>

                          {/* Headline */}
                          <h4 className="text-xl font-bold text-[#1A2B5C] tracking-wide">No Client Efficiency Data</h4>

                          {/* Optional Description */}
                          {/* <p className="text-sm text-gray-500 text-center leading-relaxed max-w-xs">
              We couldn't find any data for the selected filters. Try changing the filters or check back later.
            </p> */}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentData.map((d) => (
                    <motion.tr key={d.clientName} className="bg-white hover:bg-gray-50 transition">
                      <td className="px-4 py-2 font-medium text-gray-800">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#1A2B5C] rounded-full text-white flex items-center justify-center text-xs font-bold">
                            {d.clientName
                              ?.split(' ')
                              .map((w) => w[0])
                              .join('')
                              .slice(0, 2)}
                          </div>
                          {d.clientName}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-3 py-1 rounded-full text-md font-semibold ${
                            parseFloat(d.efficiencyPercentage) >= 80
                              ? 'bg-green-100 text-green-700'
                              : parseFloat(d.efficiencyPercentage) >= 50
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {d.efficiencyPercentage}
                        </span>
                      </td>
                      <td className="px-4 py-2">{d.biometricHours}</td>
                      <td className="px-4 py-2">{d.eprHours}</td>
                      <td className="px-4 py-2">{d.totalRevenueTarget}</td>
                      <td className="px-4 py-2">{d.totalAchieved}</td>
                      <td className="px-4 py-2">{d.achievedPercentage}</td>
                      <td className="px-4 py-2">{d.qualityPercentage}</td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination />
        </>
      )}
      {/* Pagination */}
      {/* <div className="flex flex-wrap gap-2 mt-4 justify-center">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
            >
              {'<'}
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 text-sm rounded-full border font-semibold ${
                  currentPage === page
                    ? 'bg-[#1A2B5C] text-white border-[#1A2B5C]'
                    : 'text-gray-600 border-gray-300 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
            >
              {'>'}
            </button>
          </div>
        </>
      )} */}

      {/* Focus Mode */}
      <AnimatePresence>
        {focusMode && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, transition: { type: 'spring', stiffness: 120, damping: 12 } }}
              exit={{ scale: 0.7, opacity: 0 }}
              className="bg-white rounded-3xl px-8 py-10 w-full max-w-5xl shadow-2xl relative"
            >
              <button
                onClick={() => setFocusMode(false)}
                className="absolute top-3 right-4 text-gray-400 hover:text-red-500 text-3xl font-bold"
              >
                ×
              </button>

              <h4 className="text-2xl font-extrabold text-[#1A2B5C] mb-10 text-center tracking-wide">🔥 Top 3 Clients 🔥</h4>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                {topThree.map((client, i) => {
                  const efficiency = parseFloat(client.efficiencyPercentage || 0);
                  const quality = parseFloat(client.qualityPercentage || 0);
                  const biometric = parseFloat(client.biometricHours || 0);

                  let badgeColor = 'bg-red-100 text-red-700';
                  if (efficiency >= 80) badgeColor = 'bg-green-100 text-green-700';
                  else if (efficiency >= 50) badgeColor = 'bg-yellow-100 text-yellow-700';

                  const rankBadge = [
                    { label: '🥇', color: 'text-yellow-500' },
                    { label: '🥈', color: 'text-gray-500' },
                    { label: '🥉', color: 'text-orange-400' },
                  ][i];

                  return (
                    <motion.div
                      key={client.clientName}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.2 }}
                      className="flex flex-col items-center text-center p-4 bg-white rounded-xl shadow-md border border-[#1A2B5C]/10 hover:scale-105 transition-transform duration-300 relative"
                    >
                      {/* Smaller Avatar */}
                      <div className="relative">
                        <div className="w-20 h-20 bg-gradient-to-tr from-[#1A2B5C] to-[#EE1D23] rounded-full text-white flex items-center justify-center text-lg font-bold shadow-inner ring-2 ring-offset-1 ring-[#1A2B5C]">
                          {client.clientName
                            ?.split(' ')
                            .map((w) => w[0])
                            .join('')
                            .slice(0, 2)}
                        </div>
                        <div className={`absolute -top-2 -right-2 text-xl ${rankBadge.color}`}>{rankBadge.label}</div>
                      </div>

                      <div className="mt-2 text-sm font-semibold text-[#1A2B5C]">{client.clientName}</div>

                      <div className={`mt-1 px-2 py-1 rounded-full text-[10px] font-bold ${badgeColor}`}>
                        Efficiency: {efficiency.toFixed(2)}%
                      </div>

                      {/* Smaller Stats Section */}
                      <div className="mt-2 text-xs w-full px-2">
                        <div className="text-gray-600">Quality</div>
                        <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                          <div className="bg-blue-500 h-2" style={{ width: `${Math.min(quality, 100)}%` }} />
                        </div>
                        <div className="text-[10px] text-blue-700 font-bold mt-1">{quality.toFixed(2)}%</div>

                        <div className="text-gray-600 mt-2">Biometric Hours</div>
                        <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                          <div className="bg-purple-600 h-2" style={{ width: `${Math.min((biometric / 40) * 100, 100)}%` }} />
                        </div>
                        <div className="text-[10px] text-purple-700 font-bold mt-1">{biometric.toFixed(2)} hrs</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ClientEfficiency;
